import React, { useEffect, useState } from 'react';
import { DefaultLayout } from '../layout/DefaultLayout';
import './Register.scss';
import style from './Register.scss.json';
import { ProfilePictureUploader } from 'profile-plus/lib/components/ProfilePictureUploader';
import { useAuth } from 'lincd-auth/lib/hooks/useAuth';
import { UserAccount } from 'profile-plus/lib/shapes/UserAccount';
import GoToButton from '../components/atoms/GoToButton';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '../routes';
import StepHeader from '../components/molecules/StepHeader';
import { PhoneInputFlag } from '../components/atoms/PhoneInputFlag';
import LocationInput from 'lincd-input/lib/components/LocationInput';
import { TextField } from 'lincd-input/lib/components/TextField';
import { set, SubmitHandler, useForm } from 'react-hook-form';
import { Capacitor } from '@capacitor/core';
import { Player } from 'lincd-irlcg/lib/shapes/Player';
import { asset } from 'lincd/lib/utils/LinkedFileStorage';
import { useTranslate } from '@tolgee/react';
import { Server } from 'lincd-server-utils/lib/utils/Server';
import { packageName } from '../package';
import { useSearchParams } from 'react-router-dom';
import { EventTeam } from 'lincd-irlcg/lib/shapes/EventTeam';
import { RegisterForm, RegisterFormData } from '../types/form';
import { Password } from 'lincd-auth/lib/shapes/Password';
import { Modal } from 'lincd-mui-base/lib/components/Modal';
import { useCometChat } from 'lincd-cometchat/lib/hooks/useCometChat';
import { withRetry } from 'lincd-irlcg/lib/utils/helper';
import { Button } from 'lincd-mui-base/lib/components/Button';

function Register() {
  let { t } = useTranslate();
  //with t(key,defaultValue) the key will show up on in the dev context app (hover text and hold Option) and can be added and translated in tolgee.
  let prefix = 'register';
  const navigate = useNavigate();

  const auth = useAuth<Player, UserAccount>();
  const user = auth.user;
  const account = auth.userAccount;

  const [searchParams] = useSearchParams();

  const [hasPassword, setHasPassword] = useState(false);

  const uri = searchParams.get('uri');

  const cometChat = useCometChat();

  // state for handle custom fields
  const [customForm, setCustomForm] = useState({
    phonenumber: user?.telephone || '',
    dob: user?.birthDate || '',
    city: '',
  });

  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [locationLoading, setLocationLoading] = useState(false);

  useEffect(() => {
    if (!user && !uri) {
      // if user is not logged in and is registering for an event, redirect to signin page
      navigate(ROUTES.signin.path);
    }
  },[user,uri]);
  
  const handleSignIn = () => {
    cometChat.logout();
    auth.signout();
    navigate(ROUTES.signin.path);
  };

  // custom error message for phone number
  const [customError, setCustomError] = useState({
    field: '',
    message: '',
  });

  // initial value for register form
  const formValues = {
    email: account?.email,
    emailConfirmation: '',
    password: '',
    passwordConfirmation: '',
    firstname: user?.givenName,
    lastname: user?.familyName,
    dob: user?.birthDate ? user?.birthDate.toISOString().split('T')[0] : '',
    zipcode: user?.address?.postalCode || '',
    attendance: uri ? user?.attendance || 'inPerson' : '',
  };

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    watch,
    formState: { errors, isValid },
    setError,
  } = useForm<RegisterForm>({
    defaultValues: formValues,
  });

  // if user is not logged in, redirect to signin page
  if (!user) {
    return <Navigate to={ROUTES.signin.path} replace />;
  }

  // if setup profile completed, redirect to index page
  const checkProfileCompleted = () => user?.profileSetupCompleted || false;
  if (checkProfileCompleted()) {
    return (
      <Navigate
        to={`${ROUTES.register.path}${uri ? `?uri=${uri}` : ''}`}
        replace
      />
    );
  }

  useEffect(() => {
    // get player data for phone number, address and postal code
    const getPlayerData = async () => {
      const response = await getPlayerAddressAndPhone();

      // set response to custom form
      if (response) {
        setCustomForm((prevData) => ({
          ...prevData,
          phonenumber: response.phone || '',
          city: response.address || '',
        }));
      }

      if (response?.postalCode) {
        setValue('zipcode', response.postalCode);
      }
    };

    // disable get player data for now, we assume register form is empty and user will fill it
    // getPlayerData();
    // userHasPassword();
  }, []);

  const userHasPassword = async () => {
    const response = await Password.userHasPassword(account);
    setHasPassword(response);
  };

  const getPlayerAddressAndPhone = async () => {
    return await Server.call(packageName, 'getPlayerAddressAndPhone');
  };

  const onSubmit: SubmitHandler<RegisterForm> = async (data) => {
    setLoading(true);

    // reset custom error every time user submit the form
    setCustomError({
      field: '',
      message: '',
    });

    try {
      const {
        email,
        firstname,
        lastname,
        dob: dobForm,
        zipcode,
        password,
        attendance,
      } = data;
      const { phonenumber, dob: dobDatePicker, city } = customForm;

      let birthDate: string | Date;
      if (Capacitor.getPlatform() === 'web') {
        birthDate = dobDatePicker;
      } else {
        birthDate = dobForm;
      }

      const formData: RegisterFormData = {
        givenName: firstname,
        familyName: lastname,
        email: email,
        birthDate: birthDate ? new Date(birthDate).toISOString() : null,
        telephone: phonenumber,
        password: password,
        city: city,
        postalCode: zipcode,
        attendance: attendance,
      };

      const result = await withRetry(() =>
        Server.call(packageName, 'registerUser', formData, uri),
      );

      //we need this here so that the result can be converted if an event team comes back
      const type = EventTeam;

      if (result?.errors) {
        result.errors.forEach((error) => {
          if (error.field === 'email') {
            setIsOpen(true);
            setError('email', {
              type: 'manual',
              message: error.error,
            });
          } else if (error.field === 'phonenumber' || error.field === 'city') {
            setCustomError({
              field: error.field,
              message: error.error,
            });
          }
        });
      }

      if (result?.success) {
        // for event team, after register users will be redirect to action page
        // for regular signin, will be redirect to join team page
        if (result.isEventMode) {
          // if user is in event mode, set event team
          if (result.team) {
            user.currentTeam = result.team;
          }
          // set user profile setup completed
          user.profileSetupCompleted = true;
          user.givenName = result.user.givenName;
          user.familyName = result.user.familyName;
          user.birthDate = new Date(result.user.birthDate);
          user.telephone = result.user.telephone;

          // navigate to action page
          navigate(ROUTES.index.path);
        } else {
          navigate(ROUTES.join_team.path);
        }
      }
    } catch (error) {
      console.warn(error);
      alert(
        t(
          'register.submitErrorMessage',
          "This is very exciting. Lots of people are joining to practice peace. Sadly your registration didn't go through properly. Please try again in a few moments. ",
        ),
      );
    } finally {
      setLoading(false);
    }
  };

  // add watch for city field
  useEffect(() => {
    if (customForm.city) {
      setCustomError({
        field: '',
        message: '',
      });
    }
  }, [customForm.city]);

  // handle the button disabled or not based on required fields
  const isFormValid = isValid && customForm.city;

  return (
    <DefaultLayout>
      <div
        className={style.JoinTeamContainer}
        aria-labelledby={t(prefix + '.containerLabel', 'Register Container')}
      >
        <StepHeader
          step={1}
          title={t(prefix + '.register', 'REGISTER')}
          backAction={handleSignIn}
        />
        <img
          className={style.banner}
          src={asset('/images/banners/unitybanner_2.webp')}
          alt={t(prefix + '.bannerAlt', 'Unity')}
        />
        <div
          className={style.Form}
          aria-labelledby={t(prefix + '.formLabel', 'Form')}
        >
          <form
            className={style.ContactForm}
            onSubmit={handleSubmit(onSubmit)}
            aria-labelledby={t(prefix + '.formAriaLabel', 'Contact Form')}
          >
            {uri && (
              <div className={style.bannerText}>
                <p>
                  {t(
                    prefix + '.bannerText',
                    'If you already have an account with the Peace Game then go back and sign in as usual. Once you sign in you can join this event by going to the Profile page.',
                  )}
                </p>
                <p>
                  {t(
                    prefix + '.bannerText2',
                    'Scroll down to the Current Team Section and press the “Join Team/Event” button. Choosing your event and you’re in!',
                  )}
                </p>
                <Button onClick={handleSignIn}>
                  {t(prefix + '.bannerTextButton', 'Go Back')}
                </Button>
              </div>
            )}
            <ProfilePictureUploader
              aspectRatio={3 / 2}
              thumbnailWidth={169}
              property={'profilePicture'}
              confirmText={t(prefix + '.save', 'save')}
              aria-label={t(
                prefix + '.profilePictureUploader',
                'Profile Picture Uploader',
              )}
            />
            <div className={style.FormItem}>
              <div
                className={style.inputFieldContainer}
                aria-labelledby={t(prefix + '.firstnameLabel', 'First Name')}
              >
                <label>{t(prefix + '.firstName', 'First Name')}</label>
                <div className={style.inputField}>
                  <img
                    src={asset('/images/icons/user-icon.svg')}
                    alt={t(prefix + '.userIconAlt', 'user icon')}
                  />
                  <TextField
                    name="firstname"
                    type="text"
                    className={style.Input}
                    aria-label={t(
                      prefix + '.firstNameInput',
                      'First Name input',
                    )}
                    {...register('firstname', {
                      required: t(
                        prefix + '.firstNameRequired',
                        'Please enter your first name',
                      ),
                    })}
                  />
                </div>
                {errors.firstname && (
                  <div className={style.errorValidation} aria-live="polite">
                    {errors.firstname.message}
                  </div>
                )}
              </div>

              <div
                className={style.inputFieldContainer}
                aria-labelledby={t(prefix + '.lastNameLabel', 'Last Name')}
              >
                <label>{t(prefix + '.lastname', 'Last Name')}</label>
                <div className={style.inputField}>
                  <img
                    src={asset('/images/icons/user-icon.svg')}
                    alt={t(prefix + '.userIconAlt', 'user icon')}
                  />
                  <TextField
                    name="lastname"
                    type="text"
                    className={style.Input}
                    aria-label={t(prefix + '.lastNameInput', 'Last Name input')}
                    {...register('lastname', {
                      required: t(
                        prefix + '.lastnameRequired',
                        'Please enter your last name',
                      ),
                    })}
                  />
                </div>
                {errors.lastname && (
                  <div className={style.errorValidation} aria-live="polite">
                    {errors.lastname.message}
                  </div>
                )}
              </div>

              {/* {Capacitor.getPlatform() === 'web' ? (
                <div
                  className={style.inputFieldContainer}
                  aria-labelledby={t(prefix + '.dobLabel', 'Date of birth')}
                >
                  <label>
                    {t(prefix + '.dobOptional', 'Date of birth (Optional)')}
                  </label>
                  <div className={style.inputField}>
                    <div className={style.datepickerField}>
                      <DatePicker
                        className={style.datepicker}
                        calendarClassName={style.calendar}
                        defaultValue={user?.birthDate}
                        value={customForm.dob}
                        onChange={(date: Date) => {
                          setCustomForm((prevDate) => ({
                            ...prevDate,
                            dob: date,
                          }));
                        }}
                        calendarIcon={
                          <img
                            src={asset('/images/icons/calendar-icon.svg')}
                            alt={t(
                              prefix + '.calendarIconAlt',
                              'calendar icon',
                            )}
                            className={style.calendarBig}
                          />
                        }
                        maxDate={new Date()}
                        format="dd/MM/yyyy"
                        dayPlaceholder="dd"
                        monthPlaceholder="mm"
                        yearPlaceholder="yyyy"
                        aria-label={t(prefix + '.datepicker', 'Date picker')}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className={style.inputFieldContainer}
                  aria-labelledby={t(prefix + '.dobLabel', 'Date of birth')}
                >
                  <label>
                    {t(prefix + '.dobOptional', 'Date of birth (Optional)')}
                  </label>
                  <div className={style.inputField}>
                    <img
                      src={asset('/images/icons/calendar-icon.svg')}
                      alt={t(prefix + '.calendarIconAlt', 'calendar icon')}
                      className={cl(style.calendarBig, style.inAppIcon)}
                    />
                    <TextField
                      name="dob"
                      type="date"
                      className={cl(style.Input, style.inAppInput)}
                      aria-label={t(
                        prefix + '.dobInput',
                        'Date of birth input',
                      )}
                      {...register('dob')}
                    />
                  </div>
                </div>
              )} */}

              <div
                className={style.phoneNumberControl}
                aria-labelledby={t(
                  prefix + '.phoneNumberLabel',
                  'Phone Number',
                )}
              >
                <label>
                  {t(
                    prefix + '.phoneNumberOptional',
                    'Phone Number (Optional)',
                  )}
                </label>
                <div className={style.phoneNumberField}>
                  <PhoneInputFlag
                    value={user?.telephone || ''}
                    name="phonenumber"
                    onChange={(phone) => {
                      // dont save phone number if only country code is entered
                      setCustomForm((prevFormData) => ({
                        ...prevFormData,
                        phonenumber: phone.length < 5 ? '' : phone,
                      }));
                    }}
                    aria-label={t(
                      prefix + '.phoneNumberInput',
                      'Phone number input',
                    )}
                  />
                </div>
                {customError.field === 'phonenumber' && (
                  <div
                    className={style.customErrorValidation}
                    aria-live="polite"
                  >
                    {customError.message}
                  </div>
                )}
              </div>

              <div
                className={style.inputFieldContainer}
                aria-labelledby={t(prefix + '.emailLabel', 'Email')}
              >
                <label>{t(prefix + '.email', 'Email')}</label>
                <div className={style.inputField}>
                  <img
                    src={asset('/images/icons/envelope.svg')}
                    alt={t(prefix + '.emailIconAlt', 'email icon')}
                  />
                  <TextField
                    name="email"
                    type="email"
                    className={style.Input}
                    aria-label={t(prefix + '.emailInput', 'Email input')}
                    {...register('email', {
                      required: user?.telephone
                        ? false
                        : t(
                            prefix + '.emailRequired',
                            'Please enter your email',
                          ),
                      pattern: {
                        value: /\S+@\S+\.\S+/,
                        message: t(
                          prefix + '.emailPattern',
                          'Entered value does not match email format',
                        ),
                      },
                    })}
                  />
                </div>
                {errors.email &&
                  !errors.email.message.includes('email is already in use') && (
                    <div className={style.errorValidation} aria-live="polite">
                      {errors.email.message}
                    </div>
                  )}
              </div>

              {uri ? (
                <>
                  {/* Email confirmation */}
                  {!account?.email && (
                    <div
                      className={style.inputFieldContainer}
                      aria-labelledby={t(
                        prefix + '.emailConfirmationLabel',
                        'Email Confirmation',
                      )}
                    >
                      <label>
                        {t(prefix + '.emailConfirmation', 'Email Confirmation')}
                      </label>
                      <div className={style.inputField}>
                        <img
                          src={asset('/images/icons/envelope.svg')}
                          alt={t(prefix + '.emailIconAlt', 'email icon')}
                        />
                        <TextField
                          name="email"
                          type="email"
                          className={style.Input}
                          aria-label={t(prefix + '.emailInput', 'Email input')}
                          {...register('emailConfirmation', {
                            required: user?.telephone
                              ? false
                              : t(
                                  prefix + '.emailRequired',
                                  'Please enter your email',
                                ),
                            pattern: {
                              value: /\S+@\S+\.\S+/,
                              message: t(
                                prefix + '.emailPattern',
                                'Entered value does not match email format',
                              ),
                            },
                          })}
                        />
                      </div>
                      {watch('email') !== watch('emailConfirmation') &&
                      getValues('emailConfirmation') ? (
                        <p className={style.errorValidation}>
                          {t(
                            prefix + '.unmatchedEmailError',
                            'Email did not match',
                          )}
                        </p>
                      ) : null}
                      {errors.email &&
                        !errors.email.message.includes(
                          'email is already in use',
                        ) && (
                          <div
                            className={style.errorValidation}
                            aria-live="polite"
                          >
                            {errors.email.message}
                          </div>
                        )}
                    </div>
                  )}

                  {/* Password */}
                  {!hasPassword && (
                    <>
                      <div
                        className={style.inputFieldContainer}
                        aria-labelledby={t(
                          prefix + '.passwordLabel',
                          'Password',
                        )}
                      >
                        <label>{t(prefix + '.password', 'Password')}</label>
                        <div className={style.inputField}>
                          <img
                            src={asset('/images/icons/lock.svg')}
                            alt={t(prefix + '.lockIconAlt', 'lock icon')}
                          />
                          <TextField
                            name="password"
                            type="password"
                            className={style.Input}
                            aria-label={t(
                              prefix + '.passwordInput',
                              'Password input',
                            )}
                            {...register('password', {
                              required: user?.telephone
                                ? false
                                : t(
                                    prefix + '.passwordRequired',
                                    'Please enter your password',
                                  ),
                              // pattern: {
                              //   value: /\S+@\S+\.\S+/,
                              //   message: t(
                              //     prefix + '.emailPattern',
                              //     'Entered value does not match email format',
                              //   ),
                              // },
                            })}
                          />
                        </div>

                        {errors.password && (
                          <div
                            className={style.errorValidation}
                            aria-live="polite"
                          >
                            {errors.password.message}
                          </div>
                        )}
                      </div>

                      {/* Password confirmation */}
                      <div
                        className={style.inputFieldContainer}
                        aria-labelledby={t(
                          prefix + '.passwordConfirmationLabel',
                          'Password Confirmation',
                        )}
                      >
                        <label>
                          {t(
                            prefix + '.passwordConfirmation',
                            'Password Confirmation',
                          )}
                        </label>
                        <div className={style.inputField}>
                          <img
                            src={asset('/images/icons/lock.svg')}
                            alt={t(prefix + '.lockIconAlt', 'lock icon')}
                          />
                          <TextField
                            name="password"
                            type="password"
                            className={style.Input}
                            aria-label={t(
                              prefix + '.passwordInput',
                              'Password input',
                            )}
                            {...register('passwordConfirmation', {
                              required: user?.telephone
                                ? false
                                : t(
                                    prefix + '.paswordConfirmatiolnRequired',
                                    'Please enter your password confirmation',
                                  ),
                              // pattern: {
                              //   value: /\S+@\S+\.\S+/,
                              //   message: t(
                              //     prefix + '.passwordConfirmationPattern',
                              //     'Entered value does not match password format',
                              //   ),
                              // },
                            })}
                          />
                        </div>
                        {watch('password') !== watch('passwordConfirmation') &&
                        getValues('passwordConfirmation') ? (
                          <p className={style.errorValidation}>
                            {t(
                              prefix + '.unmatchedEmailError',
                              'Password did not match',
                            )}
                          </p>
                        ) : null}
                        {errors.passwordConfirmation && (
                          <div
                            className={style.errorValidation}
                            aria-live="polite"
                          >
                            {errors.passwordConfirmation.message}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </>
              ) : null}

              <div
                className={style.inputLocation}
                aria-labelledby={t(prefix + '.locationInputLabel', 'Location')}
              >
                <LocationInput
                  of={user}
                  property={'address'}
                  multiple={false}
                  onCallback={(searchTerm) => {
                    setCustomForm((prevFormData) => ({
                      ...prevFormData,
                      city: searchTerm,
                    }));
                  }}
                  aria-label={t(prefix + '.locationInput', 'Location input')}
                  onRemoveLocation={() => {
                    // remove city from custom form
                    setCustomForm((prevFormData) => ({
                      ...prevFormData,
                      city: '',
                    }));
                  }}
                  onSearching={(isSearching) => {
                    setLocationLoading(isSearching);
                  }}
                />
                {customError.field === 'city' && (
                  <div
                    className={style.customErrorValidation}
                    aria-live="polite"
                  >
                    {customError.message}
                  </div>
                )}
              </div>

              <div
                className={style.inputFieldContainer}
                aria-labelledby={t(prefix + '.zipcodeLabel', 'Zip Code')}
              >
                <div className={style.inputField}>
                  <TextField
                    name="zipcode"
                    type="text"
                    placeholder={t(prefix + '.zipcodePlaceholder', 'Zip Code')}
                    className={style.BottomLinedInput}
                    aria-label={t(prefix + '.zipcodeInput', 'Zip code input')}
                    {...register('zipcode')}
                  />
                </div>
              </div>

              {uri && (
                <div
                  className={style.inputFieldContainer}
                  aria-labelledby={t(prefix + '.attendanceLabel', 'Attendance')}
                >
                  <p className={style.attendanceTitle}>
                    {t(prefix + '.attendance', 'Attendance')}
                  </p>
                  <div className={style.inputField}>
                    <label className={style.attendanceRadio}>
                      {t(prefix + '.inPerson', 'In Person')}
                      <input
                        type="radio"
                        value="inPerson"
                        {...register('attendance', {
                          required: t(
                            prefix + '.attendanceRequired',
                            'Please select your attendance preference',
                          ),
                        })}
                        aria-label={t(prefix + '.inPerson', 'In Person')}
                      />
                      <span className={style.checkmark}></span>
                    </label>
                    <label className={style.attendanceRadio}>
                      {t(prefix + '.home', 'Home')}

                      <input
                        type="radio"
                        value="home"
                        {...register('attendance', {
                          required: t(
                            prefix + '.attendanceRequired',
                            'Please select your attendance preference',
                          ),
                        })}
                        aria-label={t(prefix + '.home', 'Home')}
                      />
                      <span className={style.checkmark}></span>
                    </label>
                  </div>
                  {/* {t(prefix + '.home', 'Home')}
                    </label>
                  </div> */}
                  {errors.attendance && (
                    <div className={style.errorValidation} aria-live="polite">
                      {errors.attendance.message}
                    </div>
                  )}
                </div>
              )}

              <GoToButton
                fullWidth={true}
                color="primary"
                text={t(prefix + '.next', 'Next')}
                type="submit"
                loading={loading || locationLoading || !isFormValid}
                aria-label={t(prefix + '.nextButton', 'Next button')}
              />
            </div>
          </form>
        </div>
      </div>
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <div className={style.modal}>
          <h2>Already Registered</h2>
          <br />
          <p>
            The email <strong>{formValues.email}</strong> is already associated
            with an account. Please sign in instead.
          </p>
          <p>
            After signing in, go to your profile and click{' '}
            <strong>"Join Team / Event"</strong> if you're trying to join an
            event or new team.
          </p>
          <br />
          <GoToButton
            onClick={handleSignIn}
            text="Sign in"
            endIcon={false}
            className={style.modalButton}
          />
        </div>
      </Modal>
    </DefaultLayout>
  );
}

export default Register;
